/** Subscribes the TanStack issue viewer data to Alive. */
import {debounce} from '@github/mini-throttle'
import {useQueryClient} from '@github-ui/react-query'
import {useAlive} from '@github-ui/use-alive'
import useIsMounted from '@github-ui/use-is-mounted'
import {memo, useCallback, useMemo} from 'react'

import type {IssueViewerLiveUpdateChannels} from './fetch-issue-viewer-secondary-view'
import {invalidateScopes, invalidateScopesForNested, type InvalidationScope} from './secondary-view-invalidation'

const BATCH_MS = 250

/** `wait` is the server's replication lag: refetching sooner risks reading a stale replica. */
type AliveNotification = {
  wait?: number
  timestamp?: number
  reason?: string
  gid?: string
  issue_state_updated?: true
  issue_title_updated?: true
  issue_body_updated?: true
  issue_type_updated?: true
  issue_transfer_state_updated?: true
  issue_metadata_updated?: true
  issue_timeline_updated?: true
  issue_reaction_updated?: true
  parent_issue_updated?: true
  sub_issues_updated?: true
  sub_issues_summary_updated?: true
  issue_dependencies_summary_updated?: true
  issue_fields_updated?: true
  update_intent_changed?: true
  deleted_comment_id?: string
  comment_updated_id?: string
  reacted_comment_id?: string
  pinned_comment_updated_id?: string
}

function runAfterAliveWait(run: () => void, data: AliveNotification | undefined): void {
  window.setTimeout(run, data?.wait ?? 0)
}

const STATE_CHANNEL_SCOPES: readonly InvalidationScope[] = ['header']
const SUB_ISSUE_CHANNEL_SCOPES: readonly InvalidationScope[] = ['sub-issues']
/** Comment rows; the only traffic this channel is notified for. */
const TIMELINE_CHANNEL_SCOPES: readonly InvalidationScope[] = ['timeline']

const ISSUE_CHANNEL_SCOPE_BY_FIELD = {
  issue_state_updated: 'header',
  issue_title_updated: 'header',
  issue_transfer_state_updated: 'header',
  issue_type_updated: 'metadata',
  issue_metadata_updated: 'metadata',
  parent_issue_updated: 'metadata',
  issue_dependencies_summary_updated: 'metadata',
  issue_fields_updated: 'metadata',
  sub_issues_updated: 'sub-issues',
  sub_issues_summary_updated: 'sub-issues',
  issue_timeline_updated: 'timeline',
  issue_reaction_updated: 'timeline',
  deleted_comment_id: 'timeline',
  reacted_comment_id: 'timeline',
  pinned_comment_updated_id: 'pinned-comment',
} as const satisfies Partial<Record<keyof AliveNotification, InvalidationScope>>

function scopesForNotification(data: AliveNotification): readonly InvalidationScope[] {
  const scopes = new Set<InvalidationScope>()
  for (const [field, scope] of Object.entries(ISSUE_CHANNEL_SCOPE_BY_FIELD) as Array<
    [keyof typeof ISSUE_CHANNEL_SCOPE_BY_FIELD, InvalidationScope]
  >) {
    if (data[field] != null) scopes.add(scope)
  }
  return [...scopes]
}

type LiveUpdateProps = {
  channels: IssueViewerLiveUpdateChannels | undefined
  owner: string
  repo: string
  number: number
  issueId: string | undefined
  /** Set by callers whose header and sidebar render from a page payload rather than this query. */
  routeQueryKey?: readonly unknown[]
}

/** Renders nothing. A component, not a hook, because the sub-issue channels need a hook each. */
export function SecondaryViewLiveUpdates({
  channels,
  owner,
  repo,
  number,
  issueId,
  routeQueryKey,
}: LiveUpdateProps): React.JSX.Element | null {
  const queryClient = useQueryClient()
  const isMounted = useIsMounted()

  // Primitives rather than one object prop: a fresh literal each render would rebuild the throttles
  // and make `useAlive` resubscribe every render, dropping events in the gap.
  const coordinates = useMemo(() => ({owner, repo, number}), [owner, repo, number])

  const invalidateFor = useCallback(
    (scopes: readonly InvalidationScope[]) => {
      if (!isMounted() || !issueId) return
      invalidateScopes({queryClient, scopes, issueId, coordinates, routeQueryKey})
    },
    [isMounted, issueId, queryClient, coordinates, routeQueryKey],
  )

  const queueNotification = useMemo(() => {
    const queuedScopes = new Set<InvalidationScope>()
    const flush = debounce(() => {
      const scopes = [...queuedScopes]
      queuedScopes.clear()
      invalidateFor(scopes)
    }, BATCH_MS)

    return (data: AliveNotification, scopes: readonly InvalidationScope[]) => {
      if (scopes.length === 0) return
      runAfterAliveWait(() => {
        for (const scope of scopes) queuedScopes.add(scope)
        flush()
      }, data)
    }
  }, [invalidateFor])

  const invalidateNested = useMemo(
    () =>
      debounce(() => {
        if (!isMounted()) return
        invalidateScopesForNested({queryClient, scopes: SUB_ISSUE_CHANNEL_SCOPES, routeQueryKey})
      }, BATCH_MS),
    [isMounted, queryClient, routeQueryKey],
  )

  const handleIssueUpdate = useCallback(
    (data: AliveNotification) => queueNotification(data, scopesForNotification(data)),
    [queueNotification],
  )

  const handleStateUpdate = useCallback(
    (data: AliveNotification) => queueNotification(data, STATE_CHANNEL_SCOPES),
    [queueNotification],
  )

  const handleTimelineUpdate = useCallback(
    (data: AliveNotification) => queueNotification(data, TIMELINE_CHANNEL_SCOPES),
    [queueNotification],
  )

  const handleSubIssueUpdate = useCallback(
    (data: AliveNotification) => runAfterAliveWait(() => invalidateNested(), data),
    [invalidateNested],
  )

  useAlive(channels?.updated ?? null, handleIssueUpdate)
  useAlive(channels?.state ?? null, handleStateUpdate)
  useAlive(channels?.timeline ?? null, handleTimelineUpdate)

  const subIssueChannels = useMemo(() => Object.values(channels?.subIssues ?? {}), [channels?.subIssues])

  if (subIssueChannels.length === 0) return null

  return (
    <>
      {subIssueChannels.map(channel => (
        <SubIssueChannelSubscriber key={channel} channel={channel} onUpdate={handleSubIssueUpdate} />
      ))}
    </>
  )
}

// Mapped over up to 100 children, so `memo` per repo React guidance.
const SubIssueChannelSubscriber = memo(function SubIssueChannelSubscriberInner({
  channel,
  onUpdate,
}: {
  channel: string
  onUpdate: (data: AliveNotification) => void
}): null {
  useAlive(channel, onUpdate)
  return null
})
